import { useEffect, useState } from 'react';
import { useEventStore } from '../../../context/EventContext';
import { useRotationStore } from '../../../context/RotationContext';
import { getAllUniqueVoyageNumbers } from '../../../utils/rotationUtils';
import PrintDropdownComponent from '../../printer-panel/PrintDropdown';
import { HeadingTextWrapper, HeadingWrapper, LowerWrapper, PrinterPanelInputWrapper, StyledButton } from './CargoTempLogExport.style';
import { SelectOption } from '../../../types/ViewModel/SelectOption';
import useGetRotation from '../../../api/useGetRotation';
import { useVesselStore } from '../../../context/VesselContext';
import useGetLatestVoyage from '../../../api/useGetLatestVoyage';
import usePostCreateExcel from '../../../api/usePostCreateExcel';
import ExcelReportInfoFactory from '../../../factory/ExcelReportInfoFactory';
import { CargoReportEnum } from '../../../enums/cargoReportEnum';
import SpinnerLoaderComponent from '../../common/loader/SpinnerLoader';
import { CargoListItem } from '../../../types/ViewModel/CargoListItem';
import { useStowageStore } from 'src/context/StowageContext';
import { getChartererName, getFormattedCargoNumber } from 'src/utils/stowage/cargoOperationUtils';
import { eventType } from 'src/service-worker/offline_handlers/aol/enums';
import { getDateNow, isDateAfter, isDateBefore, isDateSame } from 'odfjell-date-time-utils/dateTimeUtils';
import useCargoTempLogApi from 'src/api/cargo-temp-log/useCargoTempLogApi';
import { formatAolUrlDate } from 'src/utils/aol/aolDateUtils';
import { populateCargoOnBoardFromStowage } from 'src/utils/cargo-temp-log/cargoTempLogUtils';
import { mapCargoTempLogDateBms, mapCargoTempLogDates } from 'src/factory/CargoTempLog/CargoTempLogDateFactory';
import { CreateCargoDataForExcelTemperatureReport } from '../CargoTemperatureLogUtils';
import useAppFeature from 'src/app/useAppFeature';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';

const CargoTempLogExport: React.FC = () => {
    const { rotation, cargoList } = useRotationStore();
    const { latestVoyageData, cargoOperationsEvents } = useEventStore();
    const { selectedVessel, loadingExcelFile } = useVesselStore();
    const { loadedMasterStowagePlan } = useStowageStore();
    const { isFeatureEnabled } = useAppFeature();

    const { getExtendedRotation, getCargoList } = useGetRotation();
    const { getLatestVoyage } = useGetLatestVoyage();
    const { getCargoTempLogDates } = useCargoTempLogApi();

    const { createExcel } = usePostCreateExcel();
    const { createExcelReportInfoBM } = ExcelReportInfoFactory();

    const [allVoyages, setAllVoyages] = useState<string[]>([]);
    const [charterers, setCharterers] = useState<string[]>([]);
    const [filteredCargoNumbers, setFilteredCargoNumbers] = useState<CargoListItem[]>([]);
    const [selectedVoyage, setSelectedVoyage] = useState<string>();
    const [selectedCharterers, setSelectedCharterers] = useState<string[]>([]);
    const [selectedCargoNumbers, setSelectedCargoNumbers] = useState<string[]>([]);
    const [includeAdjacent, setIncludeAdjacent] = useState<boolean>(true);
    const [usePrintableFormat, setUsePrintableFormat] = useState<boolean>(false);
    const [clearCharterers, setClearCharterers] = useState<boolean>(false);
    const [clearCargoes, setClearCargoes] = useState<boolean>(false);
    const [useOrcaWebStowage, setUseOrcaWebStowage] = useState<boolean>(false);
    const [generateReportWithOrcaWebStowage, setGenerateReportWithOrcaWebStowage] = useState<boolean>(false);

    useEffect(() => {
        if (!rotation) {
            getExtendedRotation(selectedVessel.imoNumber);
        }
        if (!latestVoyageData) {
            getLatestVoyage(selectedVessel.imoNumber);
        }
        getCargoList(selectedVessel.imoNumber);
    }, [selectedVessel]);

    useEffect(() => {
        if (!!rotation && !!latestVoyageData) {
            const voyages = getAllUniqueVoyageNumbers(rotation, latestVoyageData, true);
            setAllVoyages(voyages);
        }
    }, [rotation, latestVoyageData]);

    useEffect(() => {
        if (!selectedVoyage) {
            setCharterers([]);
            return;
        }

        let chartererListForVoyage: string[] = [];

        if (useOrcaWebStowage && loadedMasterStowagePlan?.cargoes) {
            // Use stowage plan data for newer voyages
            const stowageCargoForVoyage = loadedMasterStowagePlan.cargoes.filter((c) => c.voyageNumber === selectedVoyage);
            chartererListForVoyage = stowageCargoForVoyage.map((x) => getChartererName(x)).filter((name) => name && name.trim() !== '');
        } else if (cargoList?.length > 0) {
            // Use regular cargo list for older voyages
            const cargoForVoyage = cargoList.filter((c) => {
                const voyageFromCargo = c.cargoNumber.split('-')[0];
                return voyageFromCargo === selectedVoyage;
            });
            chartererListForVoyage = cargoForVoyage.map((x) => x.chartererName).filter((name) => name && name.trim() !== '');
        }

        const uniqueChartererNames = new Set<string>([...chartererListForVoyage]);
        const sortedCharterers = Array.from(uniqueChartererNames.values()).sort();

        setCharterers(sortedCharterers);
    }, [cargoList, selectedVoyage, useOrcaWebStowage, loadedMasterStowagePlan]);

    useEffect(() => {
        if (!selectedVoyage || !selectedCharterers || selectedCharterers.length === 0) {
            setFilteredCargoNumbers([]);
            return;
        }

        let filteredCargoes: CargoListItem[] = [];

        if (useOrcaWebStowage && loadedMasterStowagePlan?.cargoes) {
            // Use stowage plan data for newer voyages
            const stowageCargoes = loadedMasterStowagePlan.cargoes.filter(
                (c) => c.voyageNumber === selectedVoyage && getChartererName(c) && selectedCharterers.includes(getChartererName(c))
            );

            // Map RotationCargo to CargoListItem format
            filteredCargoes = stowageCargoes.map((c) => ({
                id: c.productId,
                externalId: c.cargoGuid,
                cargoNumber: getFormattedCargoNumber(c.voyageNumber, c.cargoNumber, false),
                chartererName: getChartererName(c),
                tradeName: c.product?.tradeName || '',
            }));
        } else if (cargoList && cargoList.length > 0) {
            // Use regular cargo list for older voyages
            filteredCargoes = cargoList.filter((c) => selectedCharterers.includes(c.chartererName) && c.cargoNumber.split('-')[0] === selectedVoyage);
        }

        const sortedCargoes = filteredCargoes.sort((a, b) => a.cargoNumber.localeCompare(b.cargoNumber));
        setFilteredCargoNumbers(sortedCargoes);
    }, [cargoList, selectedVoyage, selectedCharterers, useOrcaWebStowage, loadedMasterStowagePlan]);

    useEffect(() => {
        if (clearCharterers) setClearCharterers(false);
        if (clearCargoes) setClearCargoes(false);
    }, [clearCharterers, clearCargoes]);

    useEffect(() => {
        if (!generateReportWithOrcaWebStowage || !cargoOperationsEvents || !loadedMasterStowagePlan || cargoOperationsEvents.length === 0) return;

        // find start date and end date for the report based on earliest loaded cargo and latest unloaded cargo among selected cargo numbers, using cargooperations
        // the date of loading can be found by using the stowageItems in loadedmasterstowageplan.stowageitems
        const stowageItems = loadedMasterStowagePlan.stowageItems.filter((stowage) => {
            return selectedCargoNumbers.some((cargoNumber) => {
                const cargo = loadedMasterStowagePlan.cargoes.find(
                    (c) => getFormattedCargoNumber(c.voyageNumber, c.cargoNumber, false) === cargoNumber
                );
                return cargo && stowage.cargoOperation.cargoGuid === cargo.cargoGuid;
            });
        });
        const startDate = stowageItems.reduce((earliestDate, stowage) => {
            const loadingDate = cargoOperationsEvents.find((event) => {
                return (
                    event.eventTypeId === eventType.pumpingCommence &&
                    stowage.cargoOperation.operationType === 'Loading' &&
                    event.cargoOperationId === stowage.cargoOperation.imosCargoOperationId &&
                    isDateBefore(event.eventDate.actualDateTimeLocal, earliestDate)
                );
            });
            return loadingDate ? new Date(loadingDate.eventDate.actualDateTimeLocal) : earliestDate;
        }, new Date());
        let endDate = stowageItems.reduce((latestDate, stowage) => {
            const unloadingDate = cargoOperationsEvents.find((event) => {
                return (
                    event.eventTypeId === eventType.pumpingComplete &&
                    stowage.cargoOperation.operationType === 'Unloading' &&
                    event.cargoOperationId === stowage.cargoOperation.imosCargoOperationId &&
                    isDateAfter(event.eventDate.actualDateTimeLocal, latestDate)
                );
            });
            return unloadingDate ? new Date(unloadingDate.eventDate.actualDateTimeLocal) : latestDate;
        }, startDate);

        // if no unloading operations are found, use the current date as end date
        if (isDateSame(startDate, endDate, 'day')) {
            endDate = new Date();
        }

        const beforeStartDate = new Date(startDate);
        beforeStartDate.setDate(startDate.getDate() - 1);

        getCargoTempLogDates(
            selectedVessel.imoNumber,
            formatAolUrlDate(startDate),
            formatAolUrlDate(endDate),
            formatAolUrlDate(beforeStartDate),
            false,
            true
        ).then((result) => {
            if (result) {
                const excelReportInfo = createExcelReportInfoBM(
                    CargoReportEnum.CargoTemperatureLog,
                    selectedVessel.imoNumber,
                    undefined,
                    false,
                    [],
                    [],
                    undefined,
                    undefined,
                    undefined,
                    'Cargo Temperature Log',
                    undefined,
                    undefined,
                    {
                        cargoNumbers: selectedCargoNumbers.toString(),
                        voyageNumber: selectedVoyage,
                        includeAdjacentTanks: includeAdjacent,
                        printableFormat: usePrintableFormat,
                        useOrcaWebStowage: true,

                        cargoData: CreateCargoDataForExcelTemperatureReport(selectedCargoNumbers, loadedMasterStowagePlan),

                        cargoTemperatureLogDates: mapCargoTempLogDateBms(
                            populateCargoOnBoardFromStowage(
                                mapCargoTempLogDates(result),
                                loadedMasterStowagePlan,
                                // At this point generateReportWithOrcaWebStowage is true, so we know that the selected voyage is after cut-off,
                                // stowage integration should be used for all data here
                                new Date('2025-01-01'),
                                cargoOperationsEvents,
                                selectedVessel
                            )
                        ),
                    }
                );
                createExcel(excelReportInfo);
                setGenerateReportWithOrcaWebStowage(false);
            } else {
                setGenerateReportWithOrcaWebStowage(false);
            }
        });
    }, [generateReportWithOrcaWebStowage, cargoOperationsEvents, loadedMasterStowagePlan]);

    const selectVoyage = (selection: SelectOption) => {
        const voyage = selection.value;

        const cutoffVoyage = selectedVessel.firstVoyageOrcaWebStowageImosSync;
        const stowageIntegrationActive = isFeatureEnabled({
            featureEnum: AppFeatureEnum.StowageIntegration,
            imoNumber: selectedVessel.imoNumber,
            date: getDateNow(),
        });

        if (stowageIntegrationActive && voyage && cutoffVoyage && +voyage >= cutoffVoyage) {
            setUseOrcaWebStowage(true);
        } else {
            setUseOrcaWebStowage(false);
        }
        setSelectedVoyage(voyage);
        setClearCharterers(true);
        setClearCargoes(true);
        setSelectedCharterers([]);
        setSelectedCargoNumbers([]);
    };

    const selectCharterers = (selection: SelectOption[]) => {
        setSelectedCharterers(selection.map((s) => s.value));
        setClearCargoes(true);
        setSelectedCargoNumbers([]);
    };

    const selectCargoNumbers = (selection: SelectOption[]) => {
        setSelectedCargoNumbers(selection.map((s) => s.value));
    };

    const selectIncludeAdjacent = (selection: SelectOption) => {
        setIncludeAdjacent(selection.value === 'Yes');
    };

    const selectFormat = (selection: SelectOption) => {
        setUsePrintableFormat(selection.value === 'Print');
    };

    const printBtn = () => {
        if (useOrcaWebStowage) {
            setGenerateReportWithOrcaWebStowage(true);
        } else {
            const excelReportInfo = createExcelReportInfoBM(
                CargoReportEnum.CargoTemperatureLog,
                selectedVessel.imoNumber,
                undefined,
                false,
                [],
                [],
                undefined,
                undefined,
                undefined,
                'Cargo Temperature Log',
                undefined,
                undefined,
                {
                    cargoNumbers: selectedCargoNumbers.toString(),
                    voyageNumber: selectedVoyage,
                    includeAdjacentTanks: includeAdjacent,
                    printableFormat: usePrintableFormat,
                    useOrcaWebStowage: false,
                    cargoData: [],
                    cargoTemperatureLogDates: [],
                }
            );
            createExcel(excelReportInfo);
        }
    };

    const isPrintDisabled = () => {
        return (
            selectedVoyage === null ||
            selectedCharterers === null ||
            selectedCharterers.length < 1 ||
            selectedCargoNumbers === null ||
            selectedCargoNumbers.length < 1
        );
    };

    return (
        <LowerWrapper>
            <HeadingWrapper>
                <HeadingTextWrapper>
                    <h4>Cargo Temperature Log Report (Excel export)</h4>
                </HeadingTextWrapper>
            </HeadingWrapper>
            <PrinterPanelInputWrapper>
                <PrintDropdownComponent
                    heading={'Voyage'}
                    multiple={false}
                    options={allVoyages.map((v) => new SelectOption(v, v))}
                    onChange={selectVoyage}
                />
                <PrintDropdownComponent
                    heading={'Charterer(s)'}
                    multiple={true}
                    options={charterers.map((v) => new SelectOption(v, v))}
                    onChange={selectCharterers}
                    clearSelected={clearCharterers}
                />
                <PrintDropdownComponent
                    heading={'Cargoes'}
                    multiple={true}
                    options={filteredCargoNumbers.map((v) => new SelectOption(v.cargoNumber, `${v.cargoNumber} / ${v.tradeName}`))}
                    onChange={selectCargoNumbers}
                    clearSelected={clearCargoes}
                />
                <PrintDropdownComponent
                    heading={'Include adjacent tanks (where applicable)'}
                    multiple={false}
                    options={['Yes', 'No'].map((v) => new SelectOption(v, v))}
                    onChange={selectIncludeAdjacent}
                    defaultSelection={new SelectOption('Yes', 'Yes')}
                    tooltipId="cargoTempLog.ExportAdjTanks"
                />
                <PrintDropdownComponent
                    heading={'Format'}
                    multiple={false}
                    options={['Standard', 'Print'].map((v) => new SelectOption(v, v))}
                    onChange={selectFormat}
                    defaultSelection={new SelectOption('Standard', 'Standard')}
                    tooltipId="cargoTempLog.ExportFormat"
                />
                {loadingExcelFile ? (
                    <SpinnerLoaderComponent message={'report'} isFeatureSpinner={true} />
                ) : (
                    <StyledButton className="primary-btn" disabled={isPrintDisabled() || loadingExcelFile} isSelected={true} onClick={printBtn}>
                        Export
                    </StyledButton>
                )}
            </PrinterPanelInputWrapper>
        </LowerWrapper>
    );
};

export default CargoTempLogExport;
